/**
 * Legal references in the officer's language. The catalogues cite the law in English ("Federal Decree-Law No. 36 of 2023, Article 12;
 * Cabinet Resolution No. 59 of 2026, Articles 15–16 – interested parties"); in Arabic the instrument, the article and the known
 * explanatory notes are rendered in the Ministry's wording. An explanatory note with no Arabic wording is left out rather than shown in
 * English: the citation itself is what the officer relies on.
 */

/** Explanatory notes and stand-alone references, longest first when applied. */
const NOTES: Record<string, string> = {
  "the Minister's decision": "قرار الوزير",
  "every concentration decision is published with its reasons, business secrets withheld": "تنشر قرارات التركز الاقتصادي مع أسبابها دون الأسرار التجارية",
  "the Competition Regulation Committee makes recommendations to the Minister on matters arising in the application of the Decree-Law": "ترفع لجنة تنظيم المنافسة توصياتها إلى الوزير فيما يتعلق بتطبيق المرسوم بقانون",
  "referral of concentration files is Ministry practice": "وإحالة ملفات التركز الاقتصادي ممارسة للوزارة",
  // Flags of the legal rules register (services Ncp.Application/Legal/LegalRules.cs): an attribution the Department has not verified, a draft
  // it has not approved, and a period that began before the Implementing Regulations took effect.
  "not yet verified by the Department": "لم تتحقق منه الإدارة بعد",
  "verification required by the Department": "يتطلب تحقق الإدارة",
  "draft awaiting the Department's approval": "مسودة بانتظار اعتماد الإدارة",
  "Cabinet Resolution No. 59 of 2026 was not in force when this period began – the period is applied as the Department directs": "لم يكن قرار مجلس الوزراء رقم 59 لسنة 2026 نافذا عند بدء هذه المدة؛ وتطبق المدة وفق توجيه الإدارة",
  "criminal proceedings for the offences of this Decree-Law are instituted only upon the written request of the Minister or his authorised representative": "لا تحرك الدعوى الجزائية عن الجرائم الواردة في المرسوم بقانون إلا بطلب كتابي من الوزير أو من يفوضه",
  "definitions of economic concentration and control": "تعريف التركز الاقتصادي والسيطرة",
  "notification thresholds and dominance": "حدود الإخطار والهيمنة",
  "completeness of the notification": "اكتمال الإخطار",
  "confidentiality of reports and reporters": "سرية البلاغات والمبلغين",
  "confidentiality": "السرية",
  "information requests": "طلب المعلومات",
  "powers to request information": "صلاحية طلب المعلومات",
  "investigation powers": "صلاحيات التحقيق",
  "obligation to cooperate": "واجب التعاون",
  "penalties chapter": "فصل العقوبات",
  "right to be heard": "حق الاستماع",
  "interested parties": "الأطراف ذات المصلحة",
  "power of attorney": "التوكيل",
  "identity of the parties": "هوية الأطراف",
  "completeness checklist": "قائمة استيفاء المستندات",
  "appeal within 15 days of notification": "التظلم خلال 15 يوما من الإخطار",
  "judicial officers": "مأمورو الضبط القضائي",
  "coordination with sector regulators": "التنسيق مع الجهات التنظيمية القطاعية",
  "meeting controls": "ضوابط الاجتماعات",
  "vertical effects": "الآثار الرأسية",
  "substantive review controls": "ضوابط الدراسة الموضوعية",
  "counterfactual": "الوضع البديل",
  "barriers to entry, buyer power, potential competition": "عوائق الدخول والقوة الشرائية والمنافسة المحتملة",
  "initial-review clock of 10 days, extendable once": "مدة المراجعة الأولية 10 أيام قابلة للتمديد مرة واحدة",
  "market intelligence on the Article 13 file": "المعلومات السوقية في ملف المادة 13",
  "the guard against a single chain confirming itself": "ضمانة ألا يعتمد الملف على رأي جهة واحدة",
  "pre-notification guidance is informal and non-binding": "إرشادات ما قبل الإخطار غير رسمية وغير ملزمة",
  "15 working days": "15 يوم عمل",
  "90 days + 45": "90 يوما قابلة للتمديد 45 يوما",
  "extension by 45 days": "التمديد 45 يوما",
  "interruption of time limits": "انقطاع المدد",
  "acceptance of complaints": "قبول الشكاوى",
  "formal examination of complaints": "الفحص الشكلي للشكاوى",
  "receipt and registration of complaints": "تلقي الشكاوى وتسجيلها",
  "notification of the parties (30 days)": "إخطار الأطراف (30 يوما)",
  "preliminary report (20 working days for the parties' reply)": "التقرير الأولي (20 يوم عمل لرد الأطراف)",
  "answer within 5 working days": "الرد خلال 5 أيام عمل",
  "reminder cadence 20/10/7 working days": "التذكير بعد 20 و10 و7 أيام عمل",
  "ICN Recommended Practices on merger notification and review": "الممارسات الموصى بها من الشبكة الدولية للمنافسة في إخطار عمليات الاندماج ومراجعتها",
  "Ministerial resolution forming the grievance committees": "القرار الوزاري بتشكيل لجان التظلمات",
  "Relevant Market Definition Guidelines (2026)": "إرشادات تحديد السوق المعنية (2026)",
  "International best practice": "أفضل الممارسات الدولية",
  "Ministry service standard": "معيار خدمة الوزارة",
  "Ministry procedure": "إجراءات الوزارة",
  "Ministry practice": "ممارسة الوزارة",
};
const NOTE_KEYS = Object.keys(NOTES).sort((a, b) => b.length - a.length);

const INSTRUMENTS: Array<[RegExp, string | ((...m: string[]) => string)]> = [
  [/Cabinet Resolution No\.? 59 of 2026 \(Implementing Regulations of Federal Decree-Law No\.? 36 of 2023\)/g, "قرار مجلس الوزراء رقم 59 لسنة 2026 باللائحة التنفيذية"],
  [/Federal Decree-Law No\.? (\d+) of (\d{4})(?: Regulating Competition)?/g, (_m, n, y) => `المرسوم بقانون اتحادي رقم ${n} لسنة ${y}${n === "36" && y === "2023" ? " في شأن تنظيم المنافسة" : ""}`],
  [/Federal Law No\.? (\d+) of (\d{4})/g, "القانون الاتحادي رقم $1 لسنة $2"],
  [/Cabinet (?:Resolution|Decision) No\.? (\d+) of (\d{4})/g, "قرار مجلس الوزراء رقم $1 لسنة $2"],
  [/Ministerial (?:Resolution|Decision) No\.? (\d+) of (\d{4})/g, "القرار الوزاري رقم $1 لسنة $2"],
  [/(?:the )?Implementing Regulations/g, "اللائحة التنفيذية"],
  [/(?:the )?Decree-Law/g, "المرسوم بقانون"],
];

const num = String.raw`\d+(?:\(\d+\))*`;
const articleList = (list: string) => {
  const range = list.match(new RegExp(`^(${num})\\s*[–-]\\s*(${num})$`));
  if (range) return Number(range[2]) === Number(range[1]) + 1 ? `المادتان ${range[1]} و${range[2]}` : `المواد من ${range[1]} إلى ${range[2]}`;
  const items = list.split(/,\s*|\s+and\s+/).map((x) => x.trim()).filter(Boolean);
  return items.length === 2 ? `المادتان ${items[0]} و${items[1]}` : `المواد ${items.slice(0, -1).join(" و")} و${items[items.length - 1]}`;
};

const hasLatin = (s: string) => /[A-Za-z]/.test(s);

function translatePart(part: string): string | null {
  let s = part.trim();
  const whole = NOTES[s];
  if (whole) return whole;
  s = s.replace(/\s+read with\s+(?:the confidentiality duty of\s+)?/g, " مقروءة مع ");
  for (const key of NOTE_KEYS) if (s.includes(key)) s = s.split(key).join(NOTES[key]);
  for (const [re, to] of INSTRUMENTS) s = s.replace(re, to as never);
  s = s.replace(new RegExp(`Articles (${num}(?:\\s*[–-]\\s*${num}|(?:(?:,\\s*|\\s+and\\s+)${num})+))`, "g"), (_m, list: string) => articleList(list));
  s = s.replace(new RegExp(`Art(?:icle|\\.)\\s+(${num})(?:\\s*[–-]\\s*(\\(\\d+\\)))?`, "g"), (_m, a: string, b?: string) => `المادة ${a}${b ? `–${b}` : ""}`);
  s = s.replace(/Chapter (\d+)/g, "الفصل $1");
  s = s.replace(/\s+and\s+(?=المادة)/g, " و");
  s = s.replace(/,\s*/g, "، ");
  if (!hasLatin(s)) return s;
  // Notes with no Arabic wording are left out: parenthesised, after a dash or after a colon.
  s = s.replace(/\s*\([^()]*[A-Za-z][^()]*\)/g, "").replace(/\s*[–:]\s*[^–:؛]*[A-Za-z][^–:؛]*/g, "").trim();
  return hasLatin(s) || !s ? null : s;
}

/** The legal reference in the reader's language; `null` when there is nothing to show in that language. */
export function legalBasisText(text: string | null | undefined, locale: string): string | null {
  if (!text) return null;
  if (!locale.startsWith("ar")) return text;
  if (!hasLatin(text)) return text;
  const parts = text.split(/\s*;\s*/).map(translatePart).filter((p): p is string => !!p);
  return parts.length ? parts.join("؛ ") : null;
}

/** Headings of the sanitised decisions and the parts of the reference works the legal corpus is cut into. */
const SECTIONS: Record<string, string> = {
  Transaction: "الصفقة",
  "Relevant markets": "الأسواق المعنية",
  Decision: "القرار",
  Conditions: "الشروط",
  "Assessment and reasoning": "التقييم والأسباب",
  Scope: "النطاق",
  Application: "التطبيق",
  Programme: "البرنامج",
  Financing: "التمويل",
  Path: "المسار",
  "Trade sector": "قطاع التجارة",
};
const PARTS: Record<string, string> = { Question: "السؤال", Lesson: "الدرس", Template: "النموذج", Clause: "البند", Part: "الجزء", Section: "القسم", Tool: "الأداة", Volume: "المجلد" };

/** Where a passage sits in its source ("§2 Relevant markets", "Article 12", "Lesson 3"), in the reader's language; `null` when it has no Arabic wording. */
export function corpusLocatorText(locator: string | null | undefined, locale: string): string | null {
  if (!locator) return null;
  if (!locale.startsWith("ar") || !hasLatin(locator)) return locator;
  if (/^[A-Z]{2,}(?:\/[A-Z0-9]+)+$/.test(locator)) return locator;
  const s = locator.replace(/^§([\d.]+)\s+(.+)$/, (_m, n: string, name: string) => (SECTIONS[name] ? `§${n} ${SECTIONS[name]}` : `§${n}`))
    .replace(/\b(Question|Lesson|Template|Clause|Part|Section|Tool|Volume) (#?[\dIVX]+)/g, (_m, part: string, n: string) => `${PARTS[part]} ${n.replace("#", "")}`)
    .replace(/^(.+)$/, (whole) => (/(?:Article|Chapter)/.test(whole) ? legalBasisText(whole, locale) ?? whole : whole));
  return hasLatin(s) ? null : s;
}
